import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { Button, buttonVariants } from "@/components/ui/button";
import { FlipIcon } from "@/components/flashcards/Icons";
import { cn } from "@/lib/utils";

interface Props {
  text: string;
  backFace?: boolean;
  textClassName?: string;
  /**
   * `modal` keeps the study-dialog chrome; `inline` uses the interactive-element
   * card on the question face and the quiz selected surface on the answer.
   */
  variant?: "modal" | "inline";
  /** Tighter chrome when cards must share a fixed modal height. */
  density?: "comfortable" | "compact";
  /** When omitted, the flip affordance is decorative (parent handles flip). */
  onFlip?: (e: React.MouseEvent) => void;
  "aria-hidden"?: boolean;
}

const flipControlClassName = (density: "comfortable" | "compact") =>
  cn(
    "absolute hover:rotate-[15deg]",
    density === "compact" ? "bottom-2 right-2" : "bottom-3.5 right-3.5"
  );

const MODAL_FACE =
  "border-[1.5px] border-[#0d3a6f]/25 bg-gradient-to-br from-white to-[#f3f6fa] shadow-[0_12px_24px_-10px_rgba(7,37,74,0.3),0_4px_8px_-3px_rgba(13,58,111,0.16),inset_0_1px_0_rgba(255,255,255,0.9)]";

const INLINE_FACE_FRONT = "border border-primary/25 bg-primary/5";
const INLINE_FACE_BACK = "border-2 border-secondary bg-secondary/10";

const inlineFaceSurface = (backFace: boolean) =>
  backFace ? INLINE_FACE_BACK : INLINE_FACE_FRONT;

/**
 * Single face of a Lernkarte — used in the study modal and the in-lesson
 * compact flip grid.
 */
export const FlashcardCardFace = ({
  text,
  backFace = false,
  textClassName,
  variant = "modal",
  density = "comfortable",
  onFlip,
  "aria-hidden": ariaHidden,
}: Props) => {
  const faceSurface =
    variant === "modal" ? MODAL_FACE : inlineFaceSurface(backFace);
  const compact = density === "compact";
  const flipClassName = flipControlClassName(density);

  const faceRotation = backFace
    ? "[transform:rotateY(180deg)]"
    : "[transform:rotateY(0deg)]";

  return (
    <div
      aria-hidden={ariaHidden}
      className={cn(
        "absolute inset-0 flex flex-col rounded-2xl",
        faceSurface,
        "backface-hidden",
        faceRotation
      )}
    >
      <div
        className={cn(
          "flex flex-1 items-center justify-center overflow-hidden",
          compact ? "px-4 py-3" : "px-8 py-6"
        )}
      >
        <div className={cn("w-full font-semibold", textClassName)}>
          <CourseMarkdown>{text}</CourseMarkdown>
        </div>
      </div>
      {onFlip ? (
        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={onFlip}
          aria-label={backFace ? "Zur Frage" : "Karte umdrehen"}
          className={flipClassName}
        >
          <FlipIcon />
        </Button>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            buttonVariants({ size: "icon", variant: "outline" }),
            "pointer-events-none",
            flipClassName
          )}
        >
          <FlipIcon />
        </span>
      )}
    </div>
  );
};
